/**
 * Период, за который смотрят деньги.
 *
 * ОДИН НА ВЕСЬ ЭКРАН: плитки сверху и список под ними считают по одним
 * и тем же суткам. Выбрал четырнадцать — и то, и другое показывает
 * четырнадцать. Разъедься они хоть на день, и сумма расхода в плитке
 * перестанет сходиться с суммой столбца под ней, а объяснить это будет
 * нечем.
 *
 * ГОТОВЫЕ ПРОМЕЖУТКИ ШИРЕ, ЧЕМ У WILDBERRIES. У них семь, четырнадцать
 * и тридцать; здесь ещё «сегодня», «вчера», пятнадцать и девяносто.
 * «Сегодня» и «вчера» — самые частые вопросы к рекламе вообще: сколько
 * уже потратили и сколько потратили за полные сутки. Девяносто —
 * предел, глубже WB не отдаёт.
 *
 * ЗДЕСЬ ТОЛЬКО РАЗБОР. Ни сети, ни разметки.
 */

export interface Preset {
  key: string;
  title: string;
  /** Сколько последних суток, включая сегодня. */
  days: number;
}

export const PRESETS: Preset[] = [
  { key: 'today', title: 'Сегодня', days: 1 },
  { key: 'd7', title: 'Последние 7 дней', days: 7 },
  { key: 'd15', title: 'Последние 15 дней', days: 15 },
  { key: 'd30', title: 'Последние 30 дней', days: 30 },
  /* ШЕСТЬДЕСЯТ СУТОК — ПРОСЬБА ВЛАДЕЛЬЦА ОТ 28.08, и место ей именно
     здесь. Между 30 и 90 лежит разрыв втрое: за месяц сезонность
     не видна, а три месяца тянут в счёт прошлую цену товара. Шестьдесят
     — тот же срок, за который считается история ДРР в окне целей
     («по истории кабинета за 60 суток»), и одно число в двух местах
     лучше двух похожих. */
  { key: 'd60', title: 'Последние 60 дней', days: 60 },
  { key: 'd90', title: 'Последние 90 дней', days: 90 },
];

export interface Period {
  /** `YYYY-MM-DD`, включительно. */
  from: string;
  to: string;
}

/** Московская дата: сутки в отчётах Wildberries московские. */
export function mskToday(now = new Date()): Date {
  const msk = new Date(now.getTime() + 3 * 3600_000);
  return new Date(Date.UTC(msk.getUTCFullYear(), msk.getUTCMonth(), msk.getUTCDate()));
}

export function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function shift(d: Date, by: number): Date {
  const out = new Date(d);
  out.setUTCDate(out.getUTCDate() + by);
  return out;
}

/** «Последние N суток» включают сегодня. */
export function lastDays(days: number, now = new Date()): Period {
  const today = mskToday(now);
  return { from: iso(shift(today, -(days - 1))), to: iso(today) };
}

/** Вчера — отдельно: это единственный промежуток, не включающий сегодня. */
export function yesterday(now = new Date()): Period {
  const d = shift(mskToday(now), -1);
  return { from: iso(d), to: iso(d) };
}

export function daysIn(p: Period): number {
  const from = new Date(`${p.from}T00:00:00Z`).getTime();
  const to = new Date(`${p.to}T00:00:00Z`).getTime();
  return Math.floor((to - from) / 86_400_000) + 1;
}

/**
 * Как назвать выбранное.
 *
 * ГОТОВЫЙ ПРОМЕЖУТОК НАЗЫВАЕТСЯ СВОИМ ИМЕНЕМ, а не датами: «Последние
 * 30 дней» человек прочитает мгновенно, «12.07.2026 — 10.08.2026»
 * придётся вычитать. Свой промежуток — датами, иначе его не отличить
 * от готового.
 */
export function titleOf(p: Period, now = new Date()): string {
  const y = yesterday(now);
  if (p.from === y.from && p.to === y.to) return 'Вчера';

  for (const preset of PRESETS) {
    const q = lastDays(preset.days, now);
    if (q.from === p.from && q.to === p.to) return preset.title;
  }
  return `${ru(p.from)} — ${ru(p.to)}`;
}

export function ru(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  return `${d}.${m}.${y}`;
}

/**
 * Дата без года: «17.08».
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ДЛЯ УЗКОГО ЭКРАНА, И ЭТО РЕШЕНИЕ ЭТАЛОНА, А НЕ ЭКОНОМИЯ НА ГЛАЗ.
 *
 * `Bidra-Kabinet.dc.html`: `periodBtnLabel: narrow ? periodShort : period`,
 * где `periodShort = fmtS(lo) + ' — ' + fmtS(hi)`, а `fmtS` — как раз
 * день и месяц без года.
 *
 * Год в промежутке за последние семь суток не несёт ничего: человек
 * и так знает, какой сейчас год. А десять знаков на 390 — это разница
 * между кнопкой, которая влезла, и страницей, которая поехала вбок.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function руКратко(isoDate: string): string {
  const [, m, d] = isoDate.split('-');
  return `${d}.${m}`;
}

/**
 * Сутки месяца для календаря: недели с понедельника, чужие дни — `null`.
 *
 * НЕДЕЛЯ НАЧИНАЕТСЯ С ПОНЕДЕЛЬНИКА. Воскресенье первым — чужая привычка,
 * и человек, выбирающий «эту неделю», промахнётся на день.
 */
export function monthGrid(year: number, month: number): Array<string | null> {
  const first = new Date(Date.UTC(year, month, 1));
  const lead = (first.getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

  const out: Array<string | null> = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= days; d++) out.push(iso(new Date(Date.UTC(year, month, d))));
  while (out.length % 7 !== 0) out.push(null);
  return out;
}

/** Входит ли дата в промежуток. */
export function inRange(day: string, p: Period): boolean {
  return day >= p.from && day <= p.to;
}

/**
 * Период в заголовке экрана: «за последние 7 дней», «за вчера».
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ОТДЕЛЬНО ОТ `titleOf`, ПОТОМУ ЧТО ЭТО ДРУГОЙ ПАДЕЖ.
 *
 * `titleOf` даёт имя периода как строку списка — «Последние 7 дней».
 * В заголовке оно стоит после слова «Кампании» и обязано быть
 * в родительном: «Кампании за последние 7 дней», а не «Кампании
 * Последние 7 дней». Склеивать их на месте значило бы получить второе.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function периодСловами(p: Period, now = new Date()): string {
  const имя = titleOf(p, now);
  // Свой промежуток датами: «за 09.08.2026 — 15.08.2026»
  if (имя.includes('—')) return `за ${имя}`;
  return `за ${имя.charAt(0).toLowerCase()}${имя.slice(1)}`;
}

/**
 * «7 дней», «30 дней», «1 день» — СЛОВОМ ЭТАЛОНА.
 *
 * Стенд подписывает промежуток «дням»: «Кампании за последние 7 дней»,
 * «1 284 кластера за 7 дней», «Что сервис сделал бы за последние
 * 7 дней». У нас на двух экранах из трёх стояли «сутки», и в шапке
 * биддера — «дни»: одно и то же число называлось двумя словами
 * на соседних экранах. Слово выбрано не нами, а стендом.
 */
export function словоДней(n: number): string {
  const сто = n % 100;
  const десять = n % 10;
  if (сто >= 11 && сто <= 14) return 'дней';
  if (десять === 1) return 'день';
  if (десять >= 2 && десять <= 4) return 'дня';
  return 'дней';
}
